/** Kleine data-hook: laadt één endpoint, met laad-/foutstatus en refetch. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'

export interface Query<T> {
  data: T | null
  loading: boolean
  error: unknown
  refetch: () => void
}

/** 19-10-2026 (Jordy): identieke GET-verzoeken die al onderweg zijn, delen we
 *  (bijv. de wachtrij: Chats-pagina én zijbalk vragen dezelfde URL). Zo loopt
 *  een dure query nooit twee keer tegelijk vanuit dit tabblad. */
const gedeeld = new Map<string, Promise<unknown>>()

function haalGedeeld<T>(path: string): Promise<T> {
  const bezig = gedeeld.get(path)
  if (bezig) return bezig as Promise<T>
  const p = api.get<T>(path).finally(() => { gedeeld.delete(path) })
  gedeeld.set(path, p)
  return p
}

export function useApi<T>(path: string | null, deps: unknown[] = []): Query<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState<boolean>(!!path)
  const [error, setError] = useState<unknown>(null)
  const [tick, setTick] = useState(0)
  const alive = useRef(true)
  /** Volgnummer van de ophaalactie. 16-09-2026: twee snelle verversingen na
   *  elkaar (bijvoorbeeld twee keer achter elkaar een chatter kiezen op
   *  Earnings) konden in omgekeerde volgorde terugkomen; het oude antwoord
   *  overschreef dan het nieuwe en het scherm toonde weer de vorige waarde.
   *  Alleen het antwoord van de LAATSTE ophaalactie telt nog. */
  const volgnummer = useRef(0)
  /** Is er ooit een antwoord binnengekomen? Bepaalt of we mogen leeglopen. */
  const ooitGeladen = useRef(false)
  /** 19-10-2026 (Jordy): het pad dat nu onderweg is (null = niets). Een poll of
   *  refetch voor HETZELFDE pad start geen tweede verzoek zolang dit loopt: bij
   *  een trage query stapelden de 2,5 s-polls zich op de server op (de
   *  lock-opstapeling van 18/19-10). In plaats daarvan volgt precies één
   *  inhaalronde zodra het lopende verzoek klaar is. */
  const onderweg = useRef<string | null>(null)
  const nogEens = useRef(false)
  const padRef = useRef(path)
  padRef.current = path

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    if (!path) {
      setLoading(false)
      return
    }
    // Alleen bij de EERSTE ophaal de laadstaat tonen. Daarna blijven de
    // vorige gegevens staan terwijl de nieuwe onderweg zijn; anders sprong
    // elk paneel bij elke verversing terug naar "Loading…", en bij een venster
    // waar de bevraging zestien seconden kost stond het scherm dan vaker leeg
    // dan vol.
    if (!ooitGeladen.current) setLoading(true)
    setError(null)
    volgnummer.current += 1
    const mijn = volgnummer.current
    onderweg.current = path
    nogEens.current = false
    haalGedeeld<T>(path)
      .then((d) => {
        if (alive.current && mijn === volgnummer.current) {
          setData(d)
          ooitGeladen.current = true
        }
      })
      .catch((e) => {
        if (alive.current && mijn === volgnummer.current) setError(e)
      })
      .finally(() => {
        if (mijn !== volgnummer.current) return
        onderweg.current = null
        if (alive.current) setLoading(false)
        // Kwam er tijdens dit verzoek een refetch binnen: nu één keer opnieuw.
        if (alive.current && nogEens.current) {
          nogEens.current = false
          setTick((t) => t + 1)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps])

  const refetch = useCallback(() => {
    // Loopt hetzelfde verzoek nog? Niet stapelen; onthouden en straks één keer.
    if (onderweg.current !== null && onderweg.current === padRef.current) {
      nogEens.current = true
      return
    }
    setTick((t) => t + 1)
  }, [])
  return { data, loading, error, refetch }
}

/** Ververst periodiek — voor de live-blokken (feed, monitor, log). */
export function usePoll(ms: number, fn: () => void, enabled = true): void {
  const saved = useRef(fn)
  useEffect(() => {
    saved.current = fn
  }, [fn])
  useEffect(() => {
    if (!enabled || ms <= 0) return
    // 17-09-2026 (Jordy: "alles laadt enorm traag"). Een tabblad dat op de
    // achtergrond staat bleef elke paar seconden doorvragen. Met tien open
    // tabbladen op de vloer stond de server vol met werk dat niemand zag,
    // waardoor het tabblad waar wél iemand naar keek moest wachten. Op de
    // achtergrond staat de poll stil; bij terugkeren halen we meteen op, dus
    // je ziet nooit verouderde gegevens.
    const tik = () => {
      if (typeof document !== 'undefined' && document.hidden) return
      saved.current()
    }
    const id = setInterval(tik, ms)
    const bijTerugkeer = () => {
      if (typeof document !== 'undefined' && !document.hidden) saved.current()
    }
    document.addEventListener('visibilitychange', bijTerugkeer)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', bijTerugkeer)
    }
  }, [ms, enabled])
}
